<template>
  <main class="panel main" :class="mainClasses">

    <!-- Agent task bubble -->
    <AgentTaskBubble v-if="agentTaskBubbleOpen" />

    <!-- 无会话选中时的缺省状态 -->
    <section v-if="!currentSessionKey" class="main-no-session">
      <div class="main-no-session-inner">
        <div class="main-no-session-icon">💬</div>
        <div class="main-no-session-title">选择一个对话开始</div>
        <div class="main-no-session-sub">从左侧选择一个会话，或发起新的对话</div>
      </div>
    </section>

    <!-- State views -->
    <!-- Agent 对话：合并 empty/processing/result 为单一区域，避免 v-show 切换时的响应式延迟 -->
    <section
      v-if="currentSessionKey && currentSessionType === 'agent'"
      class="main-processing state-view active agent-chat-view"
    >
      <AgentStateProcessing />
    </section>

    <section
      v-if="currentSessionKey && currentSessionType !== 'agent'"
      v-show="mainState === 'empty'"
      class="main-empty state-view"
      :class="{ active: mainState === 'empty' }"
      data-state="empty"
    >
      <TelegramChat state="empty" />
    </section>

    <section
      v-if="currentSessionKey && currentSessionType !== 'agent'"
      v-show="mainState === 'processing'"
      class="main-processing state-view"
      :class="{ active: mainState === 'processing' }"
      data-state="processing"
    >
      <TelegramChat state="processing" />
    </section>

    <section
      v-if="currentSessionKey && currentSessionType !== 'agent'"
      v-show="mainState === 'result'"
      class="main-processing state-view"
      :class="{ active: mainState === 'result' }"
      data-state="result"
    >
      <TelegramChat state="result" />
    </section>

    <!-- Workspace divider -->
    <div
      class="workspace-divider"
      id="workspaceDivider"
      aria-hidden="true"
      @pointerdown.prevent="startDividerDrag"
    ></div>

    <!-- Shared workspace -->
    <SharedWorkspace />
  </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AgentStateEmpty from './AgentStateEmpty.vue'
import AgentStateProcessing from './AgentStateProcessing.vue'
import AgentStateResult from './AgentStateResult.vue'
import TelegramChat from './TelegramChat.vue'
import SharedWorkspace from './SharedWorkspace.vue'
import AgentTaskBubble from './AgentTaskBubble.vue'

export default {
  name: 'MainPanel',
  components: {
    AgentStateEmpty,
    AgentStateProcessing,
    AgentStateResult,
    TelegramChat,
    SharedWorkspace,
    AgentTaskBubble
  },
  data() {
    return {
      dividerDragging: false
    }
  },
  computed: {
    ...mapState(['mainState', 'currentSessionKey', 'currentSessionType', 'workspaceOpen', 'agentTaskBubble']),
    agentTaskBubbleOpen() {
      return this.agentTaskBubble.open
    },
    mainClasses() {
      const type = this.currentSessionType
      return {
        'telegram-session': type === 'group' || type === 'coworker',
        'telegram-group': type === 'group',
        'telegram-coworker': type === 'coworker',
        'agent-group-workspace': type === 'agent'
      }
    }
  },
  watch: {
    currentSessionKey() { this.$nextTick(() => this.focusComposer()) },
    '$store.state.currentTopicKey'() { this.$nextTick(() => this.focusComposer()) },
  },
  methods: {
    focusComposer() {
      // 延迟一帧确保组件已渲染
      setTimeout(() => {
        const ta = this.$el && this.$el.querySelector('textarea')
        if (ta) ta.focus()
      }, 50)
    },
    ...mapActions(['openAgentInsightModal', 'openAgentTaskBubble']),
    openInsight(type) {
      this.$store.dispatch('openAgentInsightModal', type)
    },
    openTaskBubble(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      this.$store.dispatch('openAgentTaskBubble', { x: Math.max(0, rect.right - 360), y: rect.bottom + 8 })
    },
    openAgentTopicDialog() {
      this.$store.commit('SET_GROUP_DIALOG', { open: true, mode: 'agent-topic', sessionKey: this.$store.state.currentSessionKey })
    },
    startDividerDrag(event) {
      // 阻止浏览器默认的 text selection / drag-start 行为
      event.preventDefault()
      this.dividerDragging = true
      const divider = event.currentTarget
      const pointerId = event.pointerId
      divider.classList.add('dragging')
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      // 捕获 pointer，即便鼠标移出 divider，事件也会继续发到它身上
      try { divider.setPointerCapture(pointerId) } catch (e) { /* noop */ }

      let rafId = null
      let pendingX = 0
      const flush = () => {
        rafId = null
        if (!this.dividerDragging) return
        const mainRect = this.$el.getBoundingClientRect()
        const nextWidth = mainRect.right - pendingX - 6
        this.setWorkspaceWidth(nextWidth)
      }

      const onPointerMove = (e) => {
        if (!this.dividerDragging) return
        e.preventDefault()
        pendingX = e.clientX
        // 用 rAF 节流，避免短时间内大量 Vuex commit + DOM 样式写入造成卡顿
        if (rafId == null) rafId = requestAnimationFrame(flush)
      }
      const stop = () => {
        this.dividerDragging = false
        if (rafId != null) { cancelAnimationFrame(rafId); rafId = null }
        divider.classList.remove('dragging')
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        try { divider.releasePointerCapture(pointerId) } catch (e) { /* noop */ }
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', stop)
        window.removeEventListener('pointercancel', stop)
      }
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', stop)
      window.addEventListener('pointercancel', stop)
    },
    setWorkspaceWidth(width) {
      const minWidth = 360
      const maxWidth = window.innerWidth * 0.65
      const clamped = Math.min(Math.max(width, minWidth), maxWidth)
      this.$store.commit('SET_WORKSPACE_WIDTH', clamped)
      // Apply via CSS variable on the app root element (same as original HTML)
      const appRoot = this.$el.closest('.app')
      if (appRoot) appRoot.style.setProperty('--workspace-col', Math.round(clamped) + 'px')
    }
  }
}
</script>
